import {
	Alert,
	AuthCaption,
	AuthDivider,
	AuthHeading,
	AuthLayout,
	AuthLeftBlock,
	AuthRightBlock,
	Button,
	Input,
	OTPInput,
} from '@rentflowdev/ui'
import { ApiError } from '@rentflowdev/api'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { oauthApi } from '../api/client'
import { RESEND_COOLDOWN_SECONDS } from '../constants'
import { completeOtpAuthorization } from '../lib/complete-authorization'
import { completeDevLogin } from '../lib/complete-dev-login'
import { emailSchema, otpCodeSchema } from '../lib/validation'
import { useOAuth } from '../providers/OAuthProvider'
import { LegalFooter } from './LegalFooter'

const DEV_LOGIN_ENABLED = import.meta.env.DEV

interface OtpAuthFormProps {
	mode: 'login' | 'register'
}

/**
 * oauth-app форма логина/регистрации — два шага (email → OTP-код). Обе формы
 * валидируются через zod-схемы из `lib/validation.ts`:
 *   - `emailSchema` — базовый `z.string().email()`, проверка on-blur + клавиша-
 *     за-клавишей для очистки подсказки, кнопка отключена пока не валидно
 *   - `otpCodeSchema` — `.length(6).regex(/^\d+$/)`, авто-сабмит только когда
 *     и длина, и формат совпали (защита от вставки «abcdef» в буфер)
 *
 * React Hook Form намеренно НЕ используется: oauth-app — отдельный маленький
 * SPA без RHF в зависимостях, ради одной двухполевой формы тянуть его излишне.
 * Валидация ложится на zod `safeParse` через `useMemo`.
 */
export function OtpAuthForm({ mode }: OtpAuthFormProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { params } = useOAuth()

	const [identifier, setIdentifier] = useState('')
	const [step, setStep] = useState<'identifier' | 'code'>('identifier')
	const [code, setCode] = useState('')
	const [sendLoading, setSendLoading] = useState(false)
	const [verifyLoading, setVerifyLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [emailError, setEmailError] = useState<string | null>(null)
	const [countdown, setCountdown] = useState(0)

	const emailIsValid = useMemo(() => emailSchema.safeParse(identifier).success, [identifier])
	const codeIsValid  = useMemo(() => otpCodeSchema.safeParse(code).success,     [code])

	useEffect(() => {
		if (countdown <= 0) return
		const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
		return () => clearTimeout(timer)
	}, [countdown])

	const handleEmailChange = (value: string) => {
		setIdentifier(value)
		// Очищаем ошибку, как только ввод стал валидным — не дёргаем по каждой клавише.
		if (emailError && emailSchema.safeParse(value).success) {
			setEmailError(null)
		}
	}

	const handleEmailBlur = () => {
		if (!identifier.trim()) return
		const result = emailSchema.safeParse(identifier)
		setEmailError(result.success ? null : t('auth.invalidEmail'))
	}

	const handleSend = async () => {
		const result = emailSchema.safeParse(identifier)
		if (!result.success) {
			setEmailError(t('auth.invalidEmail'))
			return
		}
		setEmailError(null)
		setError(null)
		setSendLoading(true)
		try {
			if (DEV_LOGIN_ENABLED && params) {
				// Dev-shortcut: пропустить OTP и завершить OAuth-флоу за один шаг.
				// Если backend ответит 403/404 (нет seeded аккаунта или non-dev сборка),
				// падаем обратно на обычный OTP.
				try {
					await completeDevLogin(identifier, params)
					return
				} catch (e) {
					console.warn('[oauth-app] dev-login failed, falling back to OTP', e)
				}
			}
			await oauthApi.sendOtp(identifier)
			setStep('code')
			setCountdown(RESEND_COOLDOWN_SECONDS)
		} catch (e) {
			// 429: too many codes asked for this address or from this network.
			setError(t(e instanceof ApiError && e.status === 429 ? 'auth.errorSendTooOften' : 'auth.errorSend'))
		} finally {
			setSendLoading(false)
		}
	}

	const handleVerify = async () => {
		if (!params) return
		if (!otpCodeSchema.safeParse(code).success) return
		setError(null)
		setVerifyLoading(true)
		try {
			await completeOtpAuthorization(identifier, code, params)
		} catch (e) {
			// 403: the code was right, but the owner blocked this team member.
			// 429: too many wrong codes; even the right one waits out the window.
			const status = e instanceof ApiError ? e.status : undefined
			setError(t(status === 403 ? 'auth.errorBlocked' : status === 429 ? 'auth.errorTooManyAttempts' : 'auth.errorVerify'))
			setCode('')
		} finally {
			setVerifyLoading(false)
		}
	}

	const resetToIdentifier = () => {
		setStep('identifier')
		setCode('')
		setError(null)
	}

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		if (codeIsValid) void handleVerify()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [codeIsValid])

	const switchPath = mode === 'login' ? '/register' : '/login'

	return (
		<AuthLayout>
			<AuthLeftBlock />

			<AuthRightBlock footer={<LegalFooter mode={mode} />}>
				{step === 'identifier' ? (
					<div className="flex flex-col w-full">
						<AuthHeading>{t(`auth.${mode}.heading`)}</AuthHeading>

						<div className="py-6 flex flex-col gap-3">
							<Input
								type="email"
								placeholder="name@example.com"
								value={identifier}
								onChange={(e) => handleEmailChange(e.target.value)}
								onBlur={handleEmailBlur}
								onKeyDown={(e) => e.key === 'Enter' && emailIsValid && void handleSend()}
								autoFocus
								autoComplete="email"
								error={emailError ?? undefined}
							/>
							{error && <Alert variant="error">{error}</Alert>}
						</div>

						<div className="flex flex-col gap-5">
							<Button
								size="full"
								onClick={() => void handleSend()}
								loading={sendLoading}
								disabled={!emailIsValid}
							>
								{t(`auth.${mode}.submit`)}
							</Button>

							<AuthDivider label={t('common.or')} />

							<Button variant="secondary" size="full" disabled>
								{t('auth.gosuslugi')}
							</Button>
						</div>

						<div className="flex items-baseline justify-center gap-1 pt-3">
							<AuthCaption>{t(`auth.${mode}.switchLabel`)}</AuthCaption>
							<Button variant="link" onClick={() => navigate(switchPath, { state: { params } })}>
								{t(`auth.${mode}.switchAction`)}
							</Button>
						</div>
					</div>
				) : (
					<div className="flex flex-col w-full gap-1">
						<AuthHeading>{t('auth.enterCode')}</AuthHeading>
						<AuthCaption className="whitespace-nowrap">
							{t('auth.codeSentTo')}{' '}
							<span className="font-medium text-(--color-foreground)">{identifier}</span>
						</AuthCaption>
						<AuthCaption>{t('auth.codeSentSubtitle')}</AuthCaption>

						<div className="py-6 flex justify-center">
							<OTPInput
								value={code}
								onChange={setCode}
								autoFocus
								disabled={verifyLoading}
								error={!!error}
							/>
						</div>

						<div className="flex flex-col gap-3">
							{error && <Alert variant="error">{error}</Alert>}
							<Button
								size="full"
								onClick={() => void handleVerify()}
								loading={verifyLoading}
								disabled={!codeIsValid}
							>
								{t(`auth.${mode}.submit`)}
							</Button>
						</div>

						<div className="flex items-center justify-center gap-1 pt-3">
							<AuthCaption>{t('auth.wrongEmail')}</AuthCaption>
							<Button variant="link" onClick={resetToIdentifier}>
								{t('auth.changeEmail')}
							</Button>
						</div>
					</div>
				)}
			</AuthRightBlock>
		</AuthLayout>
	)
}
